import React, { createContext, useCallback, useMemo, useState } from 'react';
import { modules } from '@terraframing-suite/rx-config';
import { find, get, isEqual, set } from 'lodash-es';
import { useAlerts } from '../../hooks/use-alerts';

const Context = createContext();

export const useContext = () => React.useContext(Context);

export function RunRxProvider({ children }) {
  const { showError } = useAlerts();
  const [errors, setErrors] = useState([]);
  const getError = useCallback(
    (name) => find(errors, { attribute: name })?.message,
    [errors],
  );

  const [focusedModule, setFocusedModule] = useState();
  const [selectedModules, setSelectedModules] = useState({});
  const toggleModule = useCallback(
    (module) =>
      setSelectedModules((current) => ({
        ...current,
        [module]: !current[module],
      })),
    [],
  );

  const [params, setParams] = useState();
  const getParams = useCallback(
    () => ({
      aaids: params.aaids,
      ...Object.entries(params).reduce((memo, [id, moduleParams]) => {
        if (selectedModules[id]) memo[id] = moduleParams;
        return memo;
      }, {}),
    }),
    [params, selectedModules],
  );

  const getParam = useCallback((name) => get(params, name) ?? '', [params]);
  const updateParam = useCallback(
    (name, value) => {
      if (isEqual(getParam(name), value)) return;
      setErrors((current) => current.filter((e) => e.attribute !== name));
      setParams((current) => set({ ...current }, name, value));
    },
    [getParam],
  );

  const [selectedFields, setSelectedFields] = useState([]);
  const aaids = selectedFields.map((f) => f.aaid || f.aaManagedId);

  const [isOpen, setOpen] = useState(false);
  const open = useCallback(() => {
    setParams({
      aaids,
      ...Object.fromEntries(modules.map(({ id }) => [id, {}])),
    });
    setSelectedModules({});
    setErrors([]);
    setOpen(true);
  }, [aaids]);

  const close = useCallback(() => {
    showError(null);
    setOpen(false);
  }, [showError]);

  const isValid = Object.values(selectedModules).some(Boolean);

  const state = useMemo(
    () => ({
      setSelectedFields,

      isOpen,
      open,
      close,

      focusedModule,
      setFocusedModule,
      selectedModules,
      toggleModule,

      isValid,

      getError,
      setErrors,

      getParam,
      getParams,
      updateParam,
    }),
    [
      close,
      focusedModule,
      getError,
      getParam,
      getParams,
      isOpen,
      isValid,
      open,
      selectedModules,
      toggleModule,
      updateParam,
    ],
  );

  return <Context.Provider value={state}>{children}</Context.Provider>;
}
